import clsx from 'clsx';
import {
  forwardRef,
  useCallback,
  useRef,
  useState,
  type AriaAttributes,
  type HTMLAttributes,
} from 'react';
import { useIntl } from 'react-intl';

import {
  ChevronSmallLeftDoubleIcon,
  ChevronSmallRightDoubleIcon,
  XmarkIcon,
} from '@dynatrace/strato-icons';

import { useKeepMounted } from './keepMounted.js';
import { usePageContext, useRegisterPagePanel } from './page-context.js';
import type {
  ControlledPagePanelProps,
  PagePanelSizes,
  PanelName,
  UncontrolledPagePanelProps,
} from './page-types.js';
import type { PanelSize } from './panel-types.js';
import * as panelStyles from './panel.sty.js';
import { Panel } from './Panel.js';
import * as styles from './Sidebar.sty.js';
import { useWatchBreakpoint } from './useWatchBreakpoint.js';
import { Button } from '../../buttons/button/Button.js';
import { useId } from '../../core/hooks/useId.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import type { WithChildren } from '../../core/types/with-children.js';
import { isVisualRefreshEnabled } from '../../core/utils/is-visual-refresh-enabled.js';
import { Drawer } from '../../overlays/drawer/Drawer.js';
import { Tooltip } from '../../overlays/tooltip/Tooltip.js';

/**
 * @public
 * @deprecated Deprecated along with the `Page` component. Please use `PageLayout.Sidebar` instead.
 */
export interface SidebarOwnProps
  extends WithChildren,
    Omit<HTMLAttributes<HTMLDivElement>, 'onResize' | keyof StylingProps>,
    StylingProps,
    DataTestId {
  /**
   * The desired width of the sidebar.
   * Depending on the available screen width and other panel configurations,
   * the size of the panel might not match the `preferredWidth` exactly.
   * @defaultValue 15%
   */
  preferredWidth?: PanelSize;
  /**
   * The minimum width of the sidebar in pixels.
   * @defaultValue 200
   */
  minWidth?: number;
  /**
   * Screen size below which the overlay Drawer will be shown.
   * @defaultValue 640
   */
  breakpoint?: number;
  /**
   * If the sidebar's width can be resized (by drag and drop)
   * @defaultValue true
   */
  resizable?: boolean;
  /**
   * Defines if the Sidebar content will be kept in the DOM, even if the content
   * is not shown to the user.
   * @defaultValue false
   */
  keepMounted?: boolean;
  /**
   * Notifies the user if an action happened that should change the dismissed state
   * like clicking on the backdrop on mobile.
   * The returned state is only a recommendation and might differ from what the actual
   * state of the panel should be.
   */
  onDismissChange?: (state: boolean, reason: string) => void;
  /**
   * Opt-in to the new panel controls.
   * @defaultValue false
   **/
  integratedControls?: boolean;
  /**
   * Callback triggered after resizing is finished with the new panel sizes of
   * all existing panels and the newly applied panel width (in px).
   */
  onResize?: (sizes: PagePanelSizes, width: number) => void;
}

/**
 * @public
 * @deprecated Deprecated along with the `Page` component. Please use `PageLayout.Sidebar` instead.
 */
export interface ControlledSidebarProps
  extends SidebarOwnProps,
    ControlledPagePanelProps {}

/**
 * @public
 * @deprecated Deprecated along with the `Page` component. Please use `PageLayout.Sidebar` instead.
 */
export interface UncontrolledSidebarProps
  extends SidebarOwnProps,
    UncontrolledPagePanelProps {}

/**
 * @public
 * @deprecated Deprecated along with the `Page` component. Please use `PageLayout.Sidebar` instead.
 */
export type SidebarProps = ControlledSidebarProps | UncontrolledSidebarProps;

/**
 * The `Sidebar` component is used inside the `Page` component for displaying content in the corresponding panel.
 * @public
 * @deprecated The `Page.Sidebar` component is deprecated. Please use `PageLayout.Sidebar` instead.
 */
export const Sidebar = forwardRef<HTMLDivElement, SidebarProps>(
  (props, forwardedRef) => {
    const {
      breakpoint = 640,
      className,
      defaultDismissed,
      dismissed,
      minWidth = 200,
      onDismissChange,
      onResize,
      preferredWidth = '15%',
      style,
      resizable = true,
      integratedControls,
      children,
      keepMounted = false,
      ...restProps
    }: SidebarProps = props;

    const panelId = useId();
    const panelName: PanelName = 'sidebar';
    const panelRef = useRef<HTMLDivElement>(null);
    const mergedRefs = useMergeRefs([panelRef, forwardedRef]);
    const intl = useIntl();

    const page = usePageContext();

    const panel = useRegisterPagePanel(panelName, {
      ref: panelRef,
      isControlled: dismissed !== undefined,
      isDismissed: dismissed ?? props.defaultDismissed,
      minWidth,
      breakpoint,
    });

    const setIsDismissedWithReason = useCallback(
      (value: boolean, reason: string) => {
        if (value === true) {
          page.dismissPanel(panelName);
        } else {
          page.expandPanel(panelName);
        }
        onDismissChange?.(value, reason);
      },
      [onDismissChange, page, panelName],
    );

    const aboveBreakpoint = useWatchBreakpoint({
      breakpoint,
      callback: setIsDismissedWithReason,
    });

    const panelChildren = useKeepMounted({ children, keepMounted });

    if (!panel) {
      return null;
    }

    const label = intl.formatMessage({
      defaultMessage: 'Sidebar',
      id: '9RcckxxccNTcNVf9',
    });

    const isDisplayedAsDismissed = !aboveBreakpoint || panel.isDismissed;

    return (
      <Panel
        name={panelName}
        id={panelId}
        className={clsx(
          isDisplayedAsDismissed && panelStyles.dismissedPanel,
          integratedControls && styles.integratedControlsPanel,
          styles.sidebar,
        )}
        aria-label={label}
      >
        {isDisplayedAsDismissed ? <div className={panelStyles.gap} /> : null}

        <Panel.Content
          {...restProps}
          containerSize={page.containerSize}
          ref={mergedRefs}
          label={label}
          onResize={(width) => onResize?.(page.getPanelSizes(), width)}
          preferredWidth={preferredWidth}
          minWidth={minWidth}
          handle={resizable === true ? 'right' : 'hidden'}
          isDismissed={isDisplayedAsDismissed}
          className={className}
          style={style}
        >
          {panelChildren}
        </Panel.Content>

        {integratedControls ? (
          <IntegratedControls
            dismissed={isDisplayedAsDismissed}
            onClick={({ value, reason }) => {
              setIsDismissedWithReason(value, reason);
            }}
            panelId={panelId}
            label={label}
          />
        ) : null}

        {!aboveBreakpoint ? (
          <Drawer
            className={panelStyles.drawer}
            isDismissed={panel.isDismissed}
            onDismiss={setIsDismissedWithReason}
            placement="left"
            width="wide"
          >
            {integratedControls ? (
              <div className={styles.integratedControlsDrawerHeader}>
                <Button
                  style={{ marginLeft: 'auto' }}
                  onClick={() =>
                    setIsDismissedWithReason(true, 'drawer button')
                  }
                >
                  <Button.Prefix>
                    <XmarkIcon />
                  </Button.Prefix>
                </Button>
              </div>
            ) : null}
            <div {...restProps} className={panelStyles.drawerContent}>
              {children}
            </div>
          </Drawer>
        ) : null}
      </Panel>
    );
  },
);
(Sidebar as typeof Sidebar & { displayName: string }).displayName =
  'Page.Sidebar';

/** @internal */
export interface IntegratedControlsProps extends AriaAttributes {
  dismissed: boolean;
  onClick: ({ value, reason }: { value: boolean; reason: string }) => void;
  panelId: string;
  label: string;
}

/** @internal */
export const IntegratedControls = ({
  dismissed,
  onClick,
  panelId,
  label,
}: IntegratedControlsProps) => {
  const intl = useIntl();

  const buttonRef = useRef<HTMLButtonElement>(null);
  // we need to control the tooltip state as there is a bug with popperjs, where subtree modification of the tooltip target breaks `onMouseLeave` detection.
  // For example when using a ternary to conditionally render two different icon components inside the button.
  // (i've tried using `@floating-ui/react` but it also doesnt handle this case correctly)
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const labelText = intl.formatMessage(
    dismissed
      ? { defaultMessage: 'Expand {label} panel', id: 'TWLhm3I5du5FpaM+' }
      : { defaultMessage: 'Dismiss {label} panel', id: 'SWpMc7FyaE8Uxhzn' },
    { label },
  );

  const buttonProps = dismissed
    ? ({
        'aria-expanded': 'false',
        className: styles.expandButton,
        onClick: () => {
          onClick({ value: false, reason: 'expand button' });
          setIsTooltipOpen(false);
        },
        variant: 'emphasized',
      } as const)
    : ({
        'aria-expanded': 'true',
        className: styles.dismissButton,
        onClick: () => {
          onClick({ value: true, reason: 'dismiss button' });
          setIsTooltipOpen(false);
        },
        variant: 'default',
      } as const);

  return (
    <div className={styles.integratedControls}>
      {dismissed ? (
        <button
          className={styles.hint}
          type="button"
          tabIndex={isVisualRefreshEnabled() ? -1 : undefined}
          onClick={() => {
            onClick({ value: false, reason: 'panel hint' });
            setIsTooltipOpen(false);
            buttonRef.current?.focus();
          }}
          aria-controls={panelId}
          aria-expanded={dismissed ? 'false' : 'true'}
          aria-label={labelText}
        />
      ) : null}

      <div className={styles.buttonContainer}>
        <Tooltip
          text={labelText}
          open={isTooltipOpen}
          onOpenChange={setIsTooltipOpen}
        >
          <Button
            {...buttonProps}
            ref={buttonRef}
            aria-controls={panelId}
            aria-label={labelText}
            size="condensed"
          >
            <Button.Prefix>
              {dismissed ? (
                <ChevronSmallRightDoubleIcon />
              ) : (
                <ChevronSmallLeftDoubleIcon />
              )}
            </Button.Prefix>
          </Button>
        </Tooltip>
      </div>
    </div>
  );
};
